import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthFacade } from '@iot-platform/auth';
import { LocalStorageKeys, LocalStorageService } from '@iot-platform/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { OyanUiRoutingConstants } from '../../../oyan-ui.router.constants';
import { AuthorizationService } from '../services/authorization.service';
import { OyanAuthorizationConcept } from '../types/authorization.types';

type Privileges = { [key: string]: string[] };

const hasPermission = (privileges: Privileges): boolean => Object.keys(privileges).includes(OyanAuthorizationConcept.ADMIN_PORTAL);

export const oyanAuthGuard: CanActivateFn = (): Observable<boolean> => {
  const router: Router = inject(Router);
  const authorizationService: AuthorizationService = inject(AuthorizationService);
  const authFacade: AuthFacade = inject(AuthFacade);
  const storage: LocalStorageService = inject(LocalStorageService);

  const checkInStore = (): Observable<Privileges> =>
    authFacade.privileges$.pipe(
      map((privileges: Privileges) => {
        if (!privileges && storage.get(LocalStorageKeys.STORAGE_PRIVILEGES_KEY)) {
          return JSON.parse(storage.get(LocalStorageKeys.STORAGE_PRIVILEGES_KEY));
        }
        return privileges;
      }),
      take(1)
    );

  const checkInApi = (): Observable<boolean> =>
    authorizationService.getAuthorizations().pipe(
      tap((privileges: Privileges) => authFacade.loadPrivilegesSuccess(privileges)),
      map((privileges: Privileges) => hasPermission(privileges)),
      catchError(() => of(false))
    );

  return checkInStore().pipe(
    switchMap((privileges: Privileges) => (privileges ? of(hasPermission(privileges)) : checkInApi())),
    tap((isAuthorized: boolean) => {
      if (!isAuthorized) {
        router.navigate([`/${OyanUiRoutingConstants.LOGIN}`]);
      }
    })
  );
};
